import React, { useCallback, useMemo } from 'react';
import DiamondEmpty from '@uber/base-icons/diamond-empty';
import Hide from '@uber/base-icons/hide';
import Show from '@uber/base-icons/show';
import { useStyletron } from 'baseui';
import { Button, KIND as BUTTON_KIND, SHAPE as BUTTON_SHAPE } from 'baseui/button';
import { useAtomValue, useSetAtom } from 'jotai';
import useAnalyticsForSidePanel from '../../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import type { AnnotationLabelT } from '../../../../common/types/types';
import { AnnotationFilter } from '../../../../modernization-common/side-panel';
import { useSidePanel } from '../../../../modernization-common/side-panel/side-panel-provider';
import type { AnnotationFilterEndEnhancerArgs } from '../../../../modernization-common/side-panel-m4';
import { AnnotationFilter as AnnotationFilterM3 } from '../../../../modernization-common/side-panel-m4';
import { annotationLabelsAtom, isShowAllAnnotationsVisibleAtom, isVideoTaskAtom } from '../../atoms/annotation-atoms';
import { filteredAnnotationsAtom, selectedFilterAtom } from '../../atoms/side-panel-atoms';
import { sidePanelDispatchAtom } from '../side-panel/side-panel';
import { FilterMenu } from './filter-menu';
const BUTTON_OVERRIDES = {
  BaseButton: {
    style: {
      height: 'auto',
      width: 'auto'
    }
  }
};
export const AnnotationFilterContainer = ({
  isM3SidePanelEnabled
}: {
  isM3SidePanelEnabled?: boolean;
}) => {
  const sidePanelDispatch = useSetAtom(sidePanelDispatchAtom);
  const selectedFilter = useAtomValue(selectedFilterAtom);
  const isVideoMode = useAtomValue(isVideoTaskAtom);
  const annotations = useAtomValue(filteredAnnotationsAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const isShowAllAnnotationsVisible = useAtomValue(isShowAllAnnotationsVisibleAtom);
  const {
    sendEventForToggleRenderAllAnnotations
  } = useAnalyticsForSidePanel();
  const {
    toolConfig
  } = useSidePanel();
  const [css, theme] = useStyletron();
  const getRenderIcon = useCallback((label: AnnotationLabelT) => ({
    strokeColor
  }: {
    strokeColor: string;
  }) => {
    const DisplayIcon = label.tools && label.tools[0] && toolConfig[label.tools[0]]?.Icon;
    if (DisplayIcon) {
      return <DisplayIcon strokeColor={strokeColor} />;
    }
    return <DiamondEmpty color={strokeColor} />;
  }, [toolConfig]);
  const filterOptions = useMemo(() => annotationLabels.map(label => ({
    value: label.title,
    label: label.title,
    strokeColor: label.style.strokeColor,
    renderIcon: getRenderIcon(label)
  })), [annotationLabels, getRenderIcon]);
  const onFilterChange = useCallback((filter: string) => {
    sidePanelDispatch({
      type: 'SELECT_FILTER',
      labelTitle: filter
    });
  }, [sidePanelDispatch]);
  const toggleShowAllAnnotations = useCallback(() => {
    sidePanelDispatch({
      type: 'SHOW_HIDE_ANNOTATIONS',
      allAnnotations: true,
      hidden: !isShowAllAnnotationsVisible,
      storeType: 'ANNOTATION_STORE'
    });
    sendEventForToggleRenderAllAnnotations({
      showAllAnnotationsEnabled: isShowAllAnnotationsVisible
    });
  }, [sidePanelDispatch, isShowAllAnnotationsVisible, sendEventForToggleRenderAllAnnotations]);
  const getEndEnhancer = ({
    iconStyles
  }: AnnotationFilterEndEnhancerArgs = {}) => {
    return <section className={css({
      display: 'flex',
      gap: theme.sizing.scale500
    })}>
        <Button kind={BUTTON_KIND.tertiary} shape={BUTTON_SHAPE.circle} overrides={BUTTON_OVERRIDES} onClick={toggleShowAllAnnotations} data-testid="toggle-show-all-annotations">
          {isShowAllAnnotationsVisible ? <Hide {...iconStyles} /> : <Show {...iconStyles} />}
        </Button>
        {isVideoMode && <FilterMenu iconStyles={iconStyles} />}
      </section>;
  };
  return isM3SidePanelEnabled ? <AnnotationFilterM3 selectedFilter={selectedFilter} filterOptions={filterOptions} annotationCount={annotations.length} title="Objects" placeholder="Search for an object" onFilterChange={onFilterChange} endEnhancer={getEndEnhancer} /> : <AnnotationFilter selectedFilter={selectedFilter} filterOptions={filterOptions} annotationCount={annotations.length} title="Labels" placeholder="Search for class" onFilterChange={onFilterChange} endEnhancer={getEndEnhancer} />;
};